import type {BarSeriesOption} from 'echarts';
import {EChartsOption} from 'echarts';
import {BarChart} from 'echarts/charts';
import {GridComponent, GridComponentOption, LegendComponent, TitleComponent, TooltipComponent, TooltipComponentOption} from 'echarts/components';
import * as echarts from 'echarts/core';
import {CanvasRenderer} from 'echarts/renderers';
import Vue from 'vue';
import {Component, Prop, Ref, Watch} from 'vue-property-decorator';

echarts.use([TitleComponent, TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

interface ChartSeriesData extends BarSeriesOption {
    stack?: string; // Added stack property for stacked bar charts
}

interface ChartData {
    yAxisData: string[]; // Changed from xAxisData to yAxisData for horizontal bar chart
    seriesData: ChartSeriesData[];
    title?: string;
    xAxisLabel?: string; // Changed from yAxisLabel to xAxisLabel
}

type ECOption = EChartsOption & {
    tooltip?: TooltipComponentOption;
    grid?: GridComponentOption;
};

@Component
export default class InspectionQuestionsClassificationChart extends Vue {
    @Prop({required: true, type: Object as () => ChartData})
    readonly chartData!: ChartData;

    @Ref('chartContainer')
    readonly chartContainer!: HTMLDivElement;

    private chartInstance: echarts.ECharts | null = null;

    public chartHeight: string = '140px'; // Adjusted height might be needed for horizontal bars
    public chartWidth: string = '100%';

    mounted() {
        this.initChart();
        window.addEventListener('resize', this.handleResize);
    }

    beforeDestroy() {
        if (this.chartInstance) {
            this.chartInstance.dispose();
            this.chartInstance = null;
        }
        window.removeEventListener('resize', this.handleResize);
    }

    @Watch('chartData', {deep: true})
    onChartDataChanged(newData: ChartData) {
        if (this.chartInstance && newData) {
            this.updateChartOptions();
        } else if (!newData && this.chartInstance) {
            this.chartInstance.clear();
        } else if (newData && !this.chartInstance) {
            this.initChart();
        }
    }

    private initChart(): void {
        if (this.chartContainer) {
            this.chartInstance = echarts.init(this.chartContainer);
            this.updateChartOptions();
        } else {
            console.error('Chart container element not found.');
        }
    }

    private updateChartOptions(): void {
        if (!this.chartInstance || !this.chartData) {
            return;
        }

        const option: ECOption = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {
                orient: 'horizontal',
                left: 'center',
                top: '20px',
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%', // Adjusted bottom padding
                containLabel: true,
            },
            xAxis: {
                // Changed from yAxis to xAxis
                type: 'value',
                name: this.chartData.xAxisLabel || '',
            },
            yAxis: {
                // Changed from xAxis to yAxis
                type: 'category',
                data: this.chartData.yAxisData || [],
                axisLabel: {
                    // Optional: Adjust label settings if needed
                    interval: 0,
                    // rotate: 0, // Rotation might not be needed for y-axis categories
                },
            },
            series: (this.chartData.seriesData as BarSeriesOption[]) || [],
        };

        // Ensure series data passed in includes 'stack', 'label', 'emphasis' as needed
        // The structure provided in the question implies these are set in the series data itself.

        this.chartInstance.setOption(option, true);
    }

    private handleResize(): void {
        if (this.chartInstance) {
            this.chartInstance.resize();
        }
    }

    get containerStyle() {
        return {
            width: this.chartWidth,
            height: this.chartHeight,
        };
    }
}
